import { jsxs, jsx, Fragment as Fragment$1 } from "react/jsx-runtime";
import { forwardRef, useRef, useCallback, useReducer, useEffect, Fragment } from "react";
import dedent from "dedent";
import parseHighlights from "autosuggest-highlight/parse/index.js";
import { Box } from "../Box/Box.mjs";
import { Text } from "../Text/Text.mjs";
import { Strong } from "../Strong/Strong.mjs";
import { Inline } from "../Inline/Inline.mjs";
import { Field } from "../private/Field/Field.mjs";
import { ButtonIcon } from "../ButtonIcon/ButtonIcon.mjs";
import { IconAdd } from "../icons/IconAdd/IconAdd.mjs";
import "../Text/TextContext.mjs";
import "../Heading/HeadingContext.mjs";
import "../../css/lineHeightContainer.css.mjs";
import "../../css/reset/reset.css.mjs";
import "../../css/atoms/sprinkles.css.mjs";
import "../../hooks/useIcon/icon.css.mjs";
import { touchableText } from "../../css/typography.css.mjs";
import { IconChevronLeft } from "../icons/IconChevronLeft/IconChevronLeft.mjs";
import { IconChevronRight } from "../icons/IconChevronRight/IconChevronRight.mjs";
import { textStyles } from "../../css/typography.mjs";
import { getNextIndex } from "../private/getNextIndex.mjs";
import { normalizeKey } from "../private/normalizeKey.mjs";
import { ClearField } from "../private/Field/ClearField.mjs";
import { smoothScroll } from "../private/smoothScroll.mjs";
import { useResponsiveValue } from "../useResponsiveValue/useResponsiveValue.mjs";
import { RemoveScroll } from "react-remove-scroll";
import { getSubItemId, getItemId, createAccessbilityProps } from "./createAccessbilityProps.mjs";
import "../ThemeProvider/ThemeProvider.mjs";
import "../ThemePortal/ThemePortal.mjs";
import "../ThemeProvider/ThemeContext.mjs";
import "../ThemeProvider/VanillaThemeContainer.mjs";
import "../private/DefaultMessagesContext.mjs";
import "../Box/BackgroundContext.mjs";
import "../Divider/Divider.css.mjs";
import "../Hidden/Hidden.css.mjs";
import "../../css/negativeMargin/negativeMargin.css.mjs";
import "../Accordion/AccordionContext.mjs";
import { Columns } from "../Columns/Columns.mjs";
import { Column } from "../Column/Column.mjs";
import "../private/touchable/virtualTouchable.css.mjs";
import "../private/hideFocusRings/hideFocusRings.css.mjs";
import "../Accordion/AccordionItem.css.mjs";
import "../Actions/ActionsContext.mjs";
import "../../css/textAlignedToIcon.css.mjs";
import "../Alert/Alert.css.mjs";
import "../Avatar/Avatar.css.mjs";
import "../Badge/Badge.mjs";
import "../Breadcrumb/Breadcrumb.css.mjs";
import "../Box/ColoredBox.mjs";
import "../Button/Button.mjs";
import "../ButtonLink/ButtonLink.mjs";
import "../private/Keyline/Keyline.css.mjs";
import "../Checkbox/Checkbox.mjs";
import "../Checkbox/CheckboxStandalone.mjs";
import "../ContentBlock/ContentBlock.css.mjs";
import "../DateField/DateField.mjs";
import "../DateField/DateField.css.mjs";
import "../private/Modal/Modal.mjs";
import "../private/Modal/ModalContent.mjs";
import "../private/Modal/ModalContext.mjs";
import "../TextLink/TextLink.mjs";
import "../TextLinkButton/TextLinkButton.css.mjs";
import "../private/AvoidWidowIcon/AvoidWidowIcon.css.mjs";
import "../Dropdown/Dropdown.mjs";
import "../private/MaxLines/MaxLines.css.mjs";
import "../HiddenVisually/HiddenVisually.css.mjs";
import "../Link/Link.mjs";
import "../private/defaultTextProps.mjs";
import "../List/List.css.mjs";
import "../Loader/Loader.css.mjs";
import "../Skeleton/Skeleton.css.mjs";
import "../MenuRenderer/MenuRenderer.mjs";
import "../MenuItem/MenuItem.mjs";
import "../MenuItemCheckbox/MenuItemCheckbox.mjs";
import "../MenuItemDivider/MenuItemDivider.mjs";
import "../MenuItem/MenuItemLink.mjs";
import "../OverflowMenu/OverflowMenu.css.mjs";
import "../MonthPicker/MonthPicker.mjs";
import "../Pagination/Pagination.css.mjs";
import "../PasswordField/PasswordField.mjs";
import "../Pincode/Pincode.mjs";
import "../PopoverRenderer/PopoverRenderer.mjs";
import "../Pressable/Pressable.mjs";
import "../RadioGroup/RadioGroup.mjs";
import "../RadioGroup/RadioItem.mjs";
import "../RadioGroup/RadioItemBlock.mjs";
import "../SelectableList/SelectableList.css.mjs";
import "../private/BulkActions/BulkActions.css.mjs";
import "../Spread/Spread.css.mjs";
import "../Stepper/Step.mjs";
import "../Stepper/Stepper.mjs";
import "../Tabs/Tab.mjs";
import "../Table/Table.mjs";
import "../Table/Table.css.mjs";
import "../Tabs/TabsProvider.mjs";
import "../Tabs/TabListContext.mjs";
import "../Tabs/Tabs.css.mjs";
import "../Tabs/TabPanelsContext.mjs";
import "../Tag/Tag.css.mjs";
import "../Textarea/Textarea.mjs";
import "../TextDropdown/TextDropdown.css.mjs";
import "../TextField/TextField.mjs";
import "../Tiles/Tiles.css.mjs";
import "../Toggle/Toggle.mjs";
import "../useToast/ToastContext.mjs";
import "../TooltipRenderer/TooltipRenderer.mjs";
import "../TimeField/DateSegment.css.mjs";
import "../private/FieldButtonIcon/FieldButtonIcon.mjs";
import "../MultiStepModal/MultiStepModal.mjs";
import "../SearchField/SearchField.mjs";
import "../RadioBar/RadioBarContext.mjs";
import "../RadioBar/RadioBar.css.mjs";
import "../../themes/vars.css.mjs";
import "../RadioBar/RadioBar.mjs";
import { backdrop, backdropVisible, menu, footer, groupHeading } from "./Autosuggest.css.mjs";
const INPUT_FOCUS = 0;
const INPUT_BLUR = 1;
const INPUT_CHANGE = 2;
const INPUT_ARROW_UP = 3;
const INPUT_ARROW_DOWN = 4;
const INPUT_ESCAPE = 5;
const INPUT_ENTER = 6;
const SUGGESTION_MOUSE_CLICK = 7;
const SUGGESTION_MOUSE_ENTER = 8;
const HAS_SUGGESTIONS_CHANGED = 9;
const SUGGESTION_WITH_SUBITEMS_MOUSE_CLICK = 10;
const CLEAR_SUB_ITEMS = 11;
function SuggestionItem({
  suggestion,
  highlighted,
  selected,
  onHover,
  id,
  ...restProps
}) {
  const { highlights = [], onClear, clearLabel } = suggestion;
  const label = suggestion.label ?? suggestion.text;
  const suggestionParts = parseHighlights(
    label,
    highlights.map(({ start, end }) => [start, end])
  );
  return /* @__PURE__ */ jsx(
    Box,
    {
      component: "li",
      cursor: "pointer",
      onMouseDown: (event) => {
        event.preventDefault();
      },
      onMouseMove: onHover,
      onTouchStart: onHover,
      id,
      ...restProps,
      children: /* @__PURE__ */ jsxs(
        Box,
        {
          background: highlighted ? "formAccentSoft" : void 0,
          paddingX: "xsmall",
          paddingRight: onClear ? "none" : void 0,
          paddingY: suggestion.description ? "none" : "xxsmall",
          display: "flex",
          justifyContent: "spaceBetween",
          alignItems: "center",
          children: [
            /* @__PURE__ */ jsx(Box, { className: touchableText.standard, children: /* @__PURE__ */ jsxs(Columns, { space: "small", alignY: "center", children: [
              suggestion.icon ? /* @__PURE__ */ jsx(Column, { width: "content", children: suggestion.icon }) : null,
              /* @__PURE__ */ jsxs(Column, { children: [
                /* @__PURE__ */ jsx(Text, { baseline: false, children: suggestionParts.map(
                  ({ highlight, text }, index) => selected || highlight ? /* @__PURE__ */ jsx(Strong, { children: text }, index) : /* @__PURE__ */ jsx(Fragment, { children: text }, index)
                ) }),
                suggestion.description ? /* @__PURE__ */ jsx(Text, { size: "small", tone: "secondary", baseline: false, children: suggestion.description }) : null
              ] })
            ] }) }),
            typeof onClear === "function" ? /* @__PURE__ */ jsx(
              Box,
              {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "touchable",
                height: "touchable",
                children: /* @__PURE__ */ jsx(
                  ButtonIcon,
                  {
                    id: `${id}-clear`,
                    icon: /* @__PURE__ */ jsx(IconAdd, {}),
                    tone: "secondary",
                    tabIndex: -1,
                    label: clearLabel || "Clear suggestion",
                    onClick: (event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onClear(valueFromSuggestion(suggestion));
                    }
                  }
                )
              }
            ) : null,
            /* @__PURE__ */ jsx(Inline, { align: "right", space: "none", children: suggestion.fetchSubItems || suggestion.subItems ? /* @__PURE__ */ jsx(IconChevronRight, { title: "fetch sub items", titleId: "fetchSubItems" }) : null })
          ]
        }
      )
    }
  );
}
function GroupHeading({ children }) {
  return /* @__PURE__ */ jsx(
    Box,
    {
      paddingX: "xsmall",
      className: [
        groupHeading,
        touchableText.xsmall,
        textStyles({
          size: "small",
          baseline: false,
          weight: "strong",
          tone: "formAccent"
        })
      ],
      "data-testid": process.env.NODE_ENV !== "production" ? `group-heading-${children}` : void 0,
      children
    }
  );
}
function normaliseSuggestions(suggestions) {
  let index = 0;
  const normalisedSuggestions = [];
  const groupHeadingIndexes = /* @__PURE__ */ new Map();
  const groupHeadingForSuggestion = /* @__PURE__ */ new Map();
  for (const item of suggestions) {
    if ("suggestions" in item) {
      groupHeadingIndexes.set(index, item.label);
      item.suggestions.forEach((suggestion) => {
        groupHeadingForSuggestion.set(suggestion, item.label);
      });
      index = normalisedSuggestions.push(...item.suggestions);
    } else {
      index = normalisedSuggestions.push(item);
    }
  }
  return {
    normalisedSuggestions,
    groupHeadingIndexes,
    groupHeadingForSuggestion
  };
}
function valueFromSuggestion(suggestion) {
  return "value" in suggestion ? {
    text: suggestion.text,
    value: suggestion.value
  } : { text: suggestion.text };
}
const noop = () => {
};
const fallbackValue = { text: "" };
const fallbackSuggestions = [];
function normaliseNoSuggestionMessage(noSuggestionsMessage, suggestionProp) {
  if (noSuggestionsMessage) {
    return typeof noSuggestionsMessage === "string" ? { description: noSuggestionsMessage } : noSuggestionsMessage;
  }
  if ("message" in suggestionProp) {
    const message = suggestionProp.message;
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        dedent`
          Passing \`message\` to \`suggestions\` is deprecated and will be removed in a future version. Use "noSuggestionsMessage" instead. See the documentation for usage: https://design-system.machobear.ca
             <Autosuggest
            %c-   suggestions={{ message: '${message}' }}
            %c+   noSuggestionsMessage="${message}"
             %c/>
        `,
        "color: red",
        "color: green",
        "color: inherit"
      );
    }
    return { description: message };
  }
}
const Autosuggest = forwardRef(function({
  id,
  value = fallbackValue,
  suggestions: suggestionsProp = fallbackSuggestions,
  noSuggestionsMessage: noSuggestionsMessageProp = "No options",
  onChange = noop,
  automaticSelection = true,
  showMobileBackdrop = false,
  scrollToTopOnMobile = true,
  hideSuggestionsOnSelection = true,
  onFocus = noop,
  onBlur = noop,
  placeholder,
  type = "text",
  clearLabel,
  onClear,
  // translations = autosuggest,
  ...restProps
}, forwardedRef) {
  const suggestionsPropValue = typeof suggestionsProp === "function" ? suggestionsProp(value) : suggestionsProp;
  const suggestions = Array.isArray(suggestionsPropValue) ? suggestionsPropValue : [];
  const noSuggestionsMessage = normaliseNoSuggestionMessage(
    noSuggestionsMessageProp,
    suggestionsPropValue
  );
  const hasItems = suggestions.length > 0 || Boolean(noSuggestionsMessage);
  const defaultRef = useRef(null);
  const inputRef = forwardedRef || defaultRef;
  const fireChange = useCallback(
    (suggestion) => onChange(valueFromSuggestion(suggestion)),
    [onChange]
  );
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const justPressedArrowRef = useRef(false);
  const resetPressedArrowRef = useRef(false);
  const { normalisedSuggestions, groupHeadingIndexes, groupHeadingForSuggestion } = normaliseSuggestions(suggestions);
  const suggestionCount = normalisedSuggestions.length;
  const hasSuggestions = suggestionCount > 0;
  const reducer = (state, action) => {
    const hasSubItems2 = state.subItemList.length > 0;
    const subItemsCount = state.subItemList.length;
    switch (action.type) {
      case INPUT_ARROW_DOWN: {
        if (hasSuggestions || hasSubItems2) {
          const nextIndex = getNextIndex(1, state.highlightedIndex, suggestionCount);
          const nextSubIndex = getNextIndex(1, state.highlightedSubIndex, subItemsCount);
          return {
            ...state,
            showSuggestionsIfAvailable: true,
            previewValue: hasSubItems2 ? state.subItemList[nextSubIndex] : normalisedSuggestions[nextIndex],
            highlightedIndex: hasSubItems2 ? state.highlightedIndex : nextIndex,
            highlightedSubIndex: hasSubItems2 ? nextSubIndex : 0
          };
        }
      }
      case INPUT_ARROW_UP: {
        if (hasSuggestions || hasSubItems2) {
          const nextIndex = getNextIndex(-1, state.highlightedIndex, suggestionCount);
          const nextSubIndex = getNextIndex(-1, state.highlightedSubIndex, subItemsCount);
          return {
            ...state,
            showSuggestionsIfAvailable: true,
            previewValue: hasSubItems2 ? state.subItemList[nextSubIndex] : normalisedSuggestions[nextIndex],
            highlightedIndex: hasSubItems2 ? state.highlightedIndex : nextIndex,
            highlightedSubIndex: hasSubItems2 ? nextSubIndex : 0
          };
        }
      }
      case INPUT_CHANGE: {
        return {
          ...state,
          showSuggestionsIfAvailable: true,
          inputChangedSinceFocus: true,
          previewValue: null,
          highlightedIndex: hasSubItems2 ? state.highlightedIndex : hasSuggestions && automaticSelection && value.text.length > 0 ? 0 : null
        };
      }
      case INPUT_FOCUS: {
        return {
          ...state,
          showSuggestionsIfAvailable: true,
          inputChangedSinceFocus: false,
          isFocused: true
        };
      }
      case INPUT_BLUR: {
        return {
          ...state,
          showSuggestionsIfAvailable: false,
          previewValue: null,
          highlightedIndex: null,
          isFocused: false
        };
      }
      case INPUT_ESCAPE: {
        if (value.text) {
          return {
            ...state,
            showSuggestionsIfAvailable: false,
            previewValue: null,
            highlightedIndex: null,
            highlightedSubIndex: 0
          };
        } else if (hasItems) {
          return {
            ...state,
            showSuggestionsIfAvailable: !state.showSuggestionsIfAvailable,
            previewValue: null,
            highlightedIndex: null,
            subItemList: []
          };
        }
        return state;
      }
      case INPUT_ENTER:
      case SUGGESTION_MOUSE_CLICK: {
        return {
          ...state,
          showSuggestionsIfAvailable: !hideSuggestionsOnSelection,
          previewValue: null,
          highlightedIndex: hasSubItems2 ? state.highlightedIndex : null,
          highlightedSubIndex: 0
        };
      }
      case SUGGESTION_WITH_SUBITEMS_MOUSE_CLICK: {
        return {
          ...state,
          showSuggestionsIfAvailable: true,
          previewValue: action.value[0],
          subItemList: action.value,
          highlightedSubIndex: 0
        };
      }
      case SUGGESTION_MOUSE_ENTER: {
        return {
          ...state,
          highlightedIndex: action.value,
          highlightedSubIndex: action.value
        };
      }
      case HAS_SUGGESTIONS_CHANGED: {
        return automaticSelection ? {
          ...state,
          highlightedIndex: hasSuggestions && value.text.length > 0 ? 0 : null,
          highlightedSubIndex: 0
        } : state;
      }
      case CLEAR_SUB_ITEMS: {
        return {
          ...state,
          showSuggestionsIfAvailable: true,
          isFocused: true,
          subItemList: [],
          previewValue: normalisedSuggestions[state.highlightedIndex]
        };
      }
      default: {
        console.error(new Error(`Invalid Autosuggest action: ${action}`));
        return state;
      }
    }
  };
  const [
    {
      showSuggestionsIfAvailable,
      inputChangedSinceFocus,
      previewValue,
      highlightedIndex,
      highlightedSubIndex,
      subItemList
    },
    dispatch
  ] = useReducer(reducer, {
    showSuggestionsIfAvailable: false,
    inputChangedSinceFocus: false,
    previewValue: null,
    highlightedIndex: null,
    highlightedSubIndex: 0,
    isFocused: false,
    subItemList: []
  });
  const hasSubItems = subItemList.length > 0;
  const isOpen = showSuggestionsIfAvailable && hasItems;
  const highlightedItem = typeof highlightedIndex === "number" ? document.getElementById(
    hasSubItems ? getSubItemId(id, highlightedSubIndex) : getItemId(id, highlightedIndex)
  ) : null;
  highlightedItem == null ? void 0 : highlightedItem.scrollIntoView({ block: "nearest" });
  useEffect(() => {
    dispatch({
      type: HAS_SUGGESTIONS_CHANGED
    });
  }, [hasSuggestions]);
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  });
  useEffect(() => {
    if (menuRef.current && isOpen && !isMobile) {
      const { bottom: menuBottom } = menuRef.current.getBoundingClientRect();
      const viewportHeight = document.documentElement.clientHeight;
      if (menuBottom > viewportHeight) {
        menuRef.current.scrollIntoView(false);
      }
    }
  }, [isOpen, isMobile, suggestionCount]);
  const inputProps = {
    value: previewValue ? previewValue.text : value.text,
    type: type === "search" ? type : "text",
    placeholder: !restProps.disabled ? placeholder : void 0,
    onChange: (e) => {
      const inputValue = e.target.value;
      dispatch({ type: INPUT_CHANGE });
      fireChange({ text: inputValue });
    },
    onFocus: () => {
      if (rootRef.current && scrollToTopOnMobile && isMobile) {
        smoothScroll(rootRef.current);
      }
      dispatch({ type: INPUT_FOCUS });
      onFocus();
    },
    onBlur: () => {
      if (justPressedArrowRef.current === true) {
        if (resetPressedArrowRef.current === true) {
          justPressedArrowRef.current = false;
          resetPressedArrowRef.current = false;
        }
        return;
      }
      const removeInput = (previewValue == null ? void 0 : previewValue.subItems) || (previewValue == null ? void 0 : previewValue.fetchSubItems);
      if (removeInput) {
        fireChange({ text: "" });
      } else if (previewValue) {
        fireChange(previewValue);
      } else if (isOpen && automaticSelection && inputChangedSinceFocus && value.text.length > 0 && suggestionCount > 0) {
        fireChange(normalisedSuggestions[0]);
      }
      dispatch({ type: INPUT_BLUR });
      onBlur();
    },
    onKeyDown: (event) => {
      const isSubItemPassed = Array.isArray(previewValue == null ? void 0 : previewValue.subItems) && (previewValue == null ? void 0 : previewValue.subItems.length) > 0;
      const isFetchSubItemsPassed = typeof (previewValue == null ? void 0 : previewValue.fetchSubItems) === "function";
      const targetKey = normalizeKey(event);
      if (/^Arrow(Up|Down$)/.test(targetKey)) {
        justPressedArrowRef.current = true;
        setTimeout(() => {
          justPressedArrowRef.current = false;
        }, 150);
      }
      switch (targetKey) {
        case "ArrowDown": {
          event.preventDefault();
          dispatch({ type: INPUT_ARROW_DOWN });
          return;
        }
        case "ArrowUp": {
          event.preventDefault();
          dispatch({ type: INPUT_ARROW_UP });
          return;
        }
        case "ArrowLeft": {
          if (hasSubItems) {
            event.preventDefault();
            dispatch({ type: CLEAR_SUB_ITEMS });
          }
          return;
        }
        case "ArrowRight": {
          if (isSubItemPassed) {
            return dispatch({
              type: SUGGESTION_WITH_SUBITEMS_MOUSE_CLICK,
              value: previewValue.subItems
            });
          }
          if (isFetchSubItemsPassed) {
            const fetchSubItems = previewValue.fetchSubItems;
            return fetchSubItems().then(
              (items) => dispatch({ type: SUGGESTION_WITH_SUBITEMS_MOUSE_CLICK, value: items })
            );
          }
          return;
        }
        case "Escape": {
          event.preventDefault();
          if (previewValue === null && value.text) {
            fireChange({ text: "" });
          }
          dispatch({ type: INPUT_ESCAPE });
          return;
        }
        case "Enter": {
          event.preventDefault();
          const suggestionValue = hasSubItems ? subItemList[highlightedSubIndex] : normalisedSuggestions[highlightedIndex];
          if (typeof highlightedIndex === "number" && !isSubItemPassed && !isFetchSubItemsPassed) {
            fireChange(suggestionValue);
            return dispatch({ type: INPUT_ENTER });
          }
          if (typeof highlightedIndex === "number" && isSubItemPassed) {
            return dispatch({
              type: SUGGESTION_WITH_SUBITEMS_MOUSE_CLICK,
              value: previewValue.subItems
            });
          }
          if (typeof highlightedIndex === "number" && isFetchSubItemsPassed) {
            const fetchSubItems = previewValue.fetchSubItems;
            return fetchSubItems().then(
              (items) => dispatch({ type: SUGGESTION_WITH_SUBITEMS_MOUSE_CLICK, value: items })
            );
          }
          dispatch({ type: INPUT_ENTER });
          return;
        }
        case "Tab": {
          if (isSubItemPassed || isFetchSubItemsPassed) {
            return dispatch({ type: INPUT_BLUR });
          }
          fireChange(previewValue);
          if (hasSubItems) {
            dispatch({ type: CLEAR_SUB_ITEMS });
          }
          dispatch({ type: INPUT_BLUR });
          return;
        }
        default: {
          return;
        }
      }
    }
  };
  const a11y = createAccessbilityProps({
    id,
    isOpen,
    highlightedIndex,
    highlightedSubIndex,
    hasSubItems
  });
  const clearable = Boolean(
    typeof onClear !== "undefined" && !restProps.disabled && typeof value !== "undefined" && value.text.length > 0
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    showMobileBackdrop ? /* @__PURE__ */ jsx(
      Box,
      {
        position: "fixed",
        zIndex: "dropdownBackdrop",
        transition: "fast",
        display: ["block", "none"],
        pointerEvents: isOpen ? void 0 : "none",
        top: 0,
        left: 0,
        opacity: !isOpen ? 0 : void 0,
        className: [backdrop, isOpen ? backdropVisible : void 0]
      }
    ) : null,
    /* @__PURE__ */ jsx(
      Box,
      {
        ...showMobileBackdrop && isOpen ? {
          position: "relative",
          zIndex: "dropdown"
        } : null,
        children: /* @__PURE__ */ jsx(Box, { position: "relative", ref: rootRef, children: /* @__PURE__ */ jsx(
          Field,
          {
            ...restProps,
            id,
            value: value.text,
            prefix: void 0,
            secondaryIcon: onClear ? /* @__PURE__ */ jsx(
              ClearField,
              {
                id: `${id}-clearfield`,
                hide: !clearable,
                onClear: () => {
                  onClear();
                  dispatch({ type: INPUT_ESCAPE });
                },
                label: clearLabel,
                inputRef
              }
            ) : null,
            children: (overlays, fieldProps, icon, secondaryIcon) => /* @__PURE__ */ jsxs(Fragment$1, { children: [
              /* @__PURE__ */ jsxs(Box, { width: "full", children: [
                /* @__PURE__ */ jsx(
                  Box,
                  {
                    component: "input",
                    ...fieldProps,
                    ...a11y.inputProps,
                    ...inputProps,
                    position: "relative",
                    ref: inputRef
                  }
                ),
                icon,
                /* @__PURE__ */ jsx(RemoveScroll, { ref: menuRef, enabled: isOpen, forwardProps: true, children: hasSubItems ? /* @__PURE__ */ jsxs(
                  Box,
                  {
                    display: isOpen ? "block" : "none",
                    position: "absolute",
                    zIndex: "dropdown",
                    width: "full",
                    children: [
                      /* @__PURE__ */ jsxs(
                        Box,
                        {
                          component: "ul",
                          background: !hasSuggestions && noSuggestionsMessage ? { lightMode: "white", darkMode: "neutral" } : "white",
                          cursor: !hasSuggestions && noSuggestionsMessage ? "not-allowed" : "pointer",
                          zIndex: "dropdown",
                          width: "full",
                          marginTop: "xxsmall",
                          paddingY: "xxxsmall",
                          className: menu.sub,
                          ...a11y.menuProps,
                          children: [
                            isOpen && !hasSuggestions && noSuggestionsMessage ? /* @__PURE__ */ jsxs(Box, { component: "li", paddingX: "small", className: touchableText.standard, children: [
                              noSuggestionsMessage.title ? /* @__PURE__ */ jsx(Text, { tone: "secondary", baseline: false, children: noSuggestionsMessage.title }) : null,
                              /* @__PURE__ */ jsx(Text, { tone: "secondary", size: "small", baseline: false, children: noSuggestionsMessage.description })
                            ] }) : null,
                            isOpen && hasSubItems ? /* @__PURE__ */ jsx(Fragment$1, { children: subItemList.map((suggestion, index) => {
                              const { text } = suggestion;
                              const groupHeading2 = groupHeadingIndexes.get(index);
                              return /* @__PURE__ */ jsxs(Fragment, { children: [
                                groupHeading2 ? /* @__PURE__ */ jsx(GroupHeading, { children: groupHeading2 }) : null,
                                /* @__PURE__ */ jsx(
                                  SuggestionItem,
                                  {
                                    suggestion,
                                    highlighted: highlightedSubIndex === index,
                                    selected: value === suggestion,
                                    onClick: async () => {
                                      fireChange(suggestion);
                                      dispatch({ type: SUGGESTION_MOUSE_CLICK });
                                    },
                                    onHover: () => {
                                      dispatch({
                                        type: SUGGESTION_MOUSE_ENTER,
                                        value: index
                                      });
                                    },
                                    ...a11y.getItemProps({
                                      index,
                                      label: suggestion.text,
                                      description: suggestion.description,
                                      groupHeading: groupHeadingForSuggestion.get(suggestion)
                                    })
                                  }
                                )
                              ] }, index + text);
                            }) }) : null
                          ]
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        Box,
                        {
                          padding: "xsmall",
                          background: "white",
                          className: footer,
                          justifyContent: "spaceBetween",
                          alignItems: "center",
                          children: /* @__PURE__ */ jsx(
                            Box,
                            {
                              role: "button",
                              cursor: "pointer",
                              ...inputProps,
                              onClick: () => {
                                var _a;
                                dispatch({ type: CLEAR_SUB_ITEMS });
                                (_a = inputRef.current) == null ? void 0 : _a.focus();
                              },
                              onMouseEnter: () => {
                                justPressedArrowRef.current = true;
                                resetPressedArrowRef.current = true;
                              },
                              onMouseLeave: () => {
                                justPressedArrowRef.current = false;
                                resetPressedArrowRef.current = false;
                              },
                              children: /* @__PURE__ */ jsxs(Inline, { space: "xxsmall", align: "left", alignY: "center", children: [
                                /* @__PURE__ */ jsx(IconChevronLeft, { size: "small" }),
                                /* @__PURE__ */ jsx(Text, { tone: "formAccent", weight: "strong", size: "small", children: "Back" })
                              ] })
                            }
                          )
                        }
                      )
                    ]
                  }
                ) : /* @__PURE__ */ jsxs(
                  Box,
                  {
                    component: "ul",
                    display: isOpen || isOpen && noSuggestionsMessage ? "block" : "none",
                    position: "absolute",
                    zIndex: "dropdown",
                    background: !hasSuggestions && noSuggestionsMessage ? { lightMode: "white", darkMode: "neutral" } : "white",
                    cursor: !hasSuggestions && noSuggestionsMessage ? "not-allowed" : "pointer",
                    width: "full",
                    marginTop: "xxsmall",
                    paddingY: "xxxsmall",
                    className: menu.main,
                    ...a11y.menuProps,
                    children: [
                      isOpen && !hasSuggestions && noSuggestionsMessage ? /* @__PURE__ */ jsxs(Box, { component: "li", paddingX: "small", className: touchableText.standard, children: [
                        noSuggestionsMessage.title ? /* @__PURE__ */ jsx(Text, { tone: "secondary", baseline: false, children: noSuggestionsMessage.title }) : null,
                        /* @__PURE__ */ jsx(Text, { tone: "secondary", size: "small", baseline: false, children: noSuggestionsMessage.description })
                      ] }) : null,
                      isOpen && hasSuggestions ? /* @__PURE__ */ jsx(Fragment$1, { children: normalisedSuggestions.map((suggestion, index) => {
                        const { text } = suggestion;
                        const groupHeading2 = groupHeadingIndexes.get(index);
                        return /* @__PURE__ */ jsxs(Fragment, { children: [
                          groupHeading2 ? /* @__PURE__ */ jsx(GroupHeading, { children: groupHeading2 }) : null,
                          /* @__PURE__ */ jsx(
                            SuggestionItem,
                            {
                              suggestion,
                              highlighted: highlightedIndex === index,
                              selected: value === suggestion,
                              onClick: async () => {
                                if (suggestion.fetchSubItems || suggestion.subItems) {
                                  const items = suggestion.fetchSubItems ? await suggestion.fetchSubItems() : suggestion.subItems;
                                  return dispatch({
                                    type: SUGGESTION_WITH_SUBITEMS_MOUSE_CLICK,
                                    value: items
                                  });
                                }
                                return fireChange(suggestion), dispatch({ type: SUGGESTION_MOUSE_CLICK });
                              },
                              onHover: () => {
                                dispatch({
                                  type: SUGGESTION_MOUSE_ENTER,
                                  value: index
                                });
                              },
                              ...a11y.getItemProps({
                                index,
                                label: suggestion.label ?? suggestion.text,
                                description: suggestion.description,
                                groupHeading: groupHeadingForSuggestion.get(suggestion)
                              })
                            }
                          )
                        ] }, index + text);
                      }) }) : null
                    ]
                  }
                ) }),
                overlays
              ] }),
              secondaryIcon
            ] })
          }
        ) })
      }
    )
  ] });
});
Autosuggest.displayName = "Autosuggest";
export {
  Autosuggest
};
